/**
 * Detects whether this package was loaded more than once.
 *
 * The core is declared as a Module Federation singleton in each app that uses it, so a provider
 * mounted in the host is visible to components rendering inside a remote. When that wiring is wrong
 * the remote bundles its own copy, which means its own React context. Anything that then calls
 * `useLocalizationContext` throws — but only at the point of use, and only if that copy of the
 * module is actually imported. Counting evaluations on `globalThis` (under a registered symbol,
 * the one place two separately-bundled copies can still see each other) makes the duplicate load
 * itself loud, as soon as the second copy is evaluated.
 */

const INSTANCE_COUNT_KEY: unique symbol = Symbol.for(
  '@brightdrop/localization-core.instanceCount'
);

type GuardedGlobal = typeof globalThis & {
  [INSTANCE_COUNT_KEY]?: number;
};

const guardedGlobal = globalThis as GuardedGlobal;

guardedGlobal[INSTANCE_COUNT_KEY] =
  (guardedGlobal[INSTANCE_COUNT_KEY] ?? 0) + 1;

type ViteHot = { dispose: (callback: () => void) => void };

// Fast Refresh re-evaluates this module in the same realm. Drop the count we just added so the
// replacement load is not treated as a second copy; a real federated duplicate has no dispose.
const hot = (import.meta as ImportMeta & { hot?: ViteHot }).hot;
hot?.dispose(() => {
  const current = guardedGlobal[INSTANCE_COUNT_KEY] ?? 0;
  guardedGlobal[INSTANCE_COUNT_KEY] = Math.max(0, current - 1);
});

/** How many times this module has been evaluated in the current realm. Should always be 1. */
export const getInstanceCount = (): number =>
  guardedGlobal[INSTANCE_COUNT_KEY] ?? 0;

const buildMessage = (count: number): string =>
  `@brightdrop/localization-core was loaded ${count} times, but it must be a single shared instance. ` +
  "Check that every app importing it declares '@brightdrop/localization-core': { singleton: true } " +
  'in the shared block of its vite.config.mjs. A second copy has its own React context, so anything ' +
  'that calls useLocalizationContext inside a federated remote will throw.';

/**
 * Reports a duplicate load. Throws in development so it cannot be ignored. Logs in production so
 * the host can still boot; the remote then throws at `useLocalizationContext`, which is the actual
 * failure, rather than this guard taking the whole page down first.
 */
const assertSingleInstance = (): void => {
  const count = getInstanceCount();
  if (count <= 1) return;

  const message = buildMessage(count);
  if (process.env.NODE_ENV === 'production') {
    console.error(message);
    return;
  }
  throw new Error(message);
};

// Remotes load lazily, long after the host has mounted. Asserting here — at evaluation, not in a
// provider effect — is what actually sees the second copy.
assertSingleInstance();
